import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OneClickUpsell } from '../../offer.types';
import { formatCurrency } from '../../utils/formatting';

interface UpsellPopupProps {
  upsell: OneClickUpsell;
  /** Cliente clicou em aceitar — deve cobrar e resolver. Retorna mensagem de erro curta (ou null em sucesso). */
  onAccept: () => Promise<string | null>;
  /** Cliente recusou (botão ou Esc). */
  onDecline: () => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Popup de upsell one-click. Aparece após o cartão ser aprovado e antes do redirect.
 * Overlay próprio em Tailwind (sem MUI), acessível: role="dialog", aria-modal, Esc recusa,
 * trap de foco básico. Não loga PII.
 */
const UpsellPopup: React.FC<UpsellPopupProps> = ({ upsell, onAccept, onDecline }) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAccept = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const error = await onAccept();
    if (error) {
      // Decisão do dono: mostra erro curto e o onAccept já segue para o thank-you.
      setErrorMessage(error);
    }
    // Em sucesso, o parent fecha o popup (desmonta). Mantém processing para travar nova ação.
  }, [isProcessing, onAccept]);

  const handleDecline = useCallback(() => {
    if (isProcessing) return;
    onDecline();
  }, [isProcessing, onDecline]);

  // Foco inicial no botão aceitar + trap de foco + Esc para recusar.
  useEffect(() => {
    acceptButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleDecline();
        return;
      }

      if (event.key !== 'Tab') return;

      const container = dialogRef.current;
      if (!container) return;
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleDecline]);

  const hasCompareAt =
    typeof upsell.compareAtPrice === 'number' && upsell.compareAtPrice > upsell.price;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop — clique não fecha (decisão explícita do cliente) */}
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upsell-title"
        aria-describedby={upsell.description ? 'upsell-description' : undefined}
        className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl p-6 text-center"
      >
        {upsell.imageUrl && (
          <img
            src={upsell.imageUrl}
            alt=""
            className="mx-auto mb-4 h-40 w-full max-w-xs rounded-xl object-contain"
            loading="eager"
          />
        )}

        <h2
          id="upsell-title"
          className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2"
        >
          {upsell.title}
        </h2>

        {upsell.description && (
          <p
            id="upsell-description"
            className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed"
          >
            {upsell.description}
          </p>
        )}

        <div className="mb-5 flex items-baseline justify-center gap-2">
          {hasCompareAt && (
            <span className="text-base text-gray-400 line-through">
              {formatCurrency(upsell.compareAtPrice as number)}
            </span>
          )}
          <span className="text-3xl font-extrabold text-gray-900 dark:text-gray-50">
            {formatCurrency(upsell.price)}
          </span>
        </div>

        {errorMessage && (
          <p role="alert" className="mb-3 text-sm text-red-500">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <button
            ref={acceptButtonRef}
            type="button"
            onClick={handleAccept}
            disabled={isProcessing}
            aria-busy={isProcessing}
            className="w-full text-white font-semibold py-3 px-6 rounded-lg text-base transition duration-150 btn-primary disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {isProcessing && (
              <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
            )}
            {isProcessing ? 'Processando...' : upsell.acceptButtonText}
          </button>

          <button
            type="button"
            onClick={handleDecline}
            disabled={isProcessing}
            className="w-full bg-transparent border border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-300 font-medium py-3 px-6 rounded-lg text-base transition duration-150 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {upsell.declineButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpsellPopup;
